<template>
  <BaseModalEditCreate
    mode="add"
    :is-modified="false"
    :close-on-outside-click="false"
    max-width="extra"
    @dismiss-changes="emit('close')"
    @save="emitSelectedRows = true">
    <template #form>
      <div class="overflow-hidden h-full grid grid-rows-[auto_1fr]">
        <div class="flex w-full justify-between items-center mb-1">
          <p class="font-light">{{ $t('messages.info.ctrlClickToAddItems') }}</p>
          <button
            type="button"
            :disabled="list.length === 0"
            @click="isAddFilteredModalActive = true"
            class="px-2 py-1 font-normal border border-gray-300 rounded-md"
            :class="{ 'opacity-50': list.length === 0 }">
            <span class="flex items-center justify-center gap-2 text-sm capitalize text-theme-500">
              <IconAddListOutline class="size-4" />

              <p>
                {{ tableQuery ? $t('common.actions.addFiltered') : $t('common.actions.addAll') }}
              </p>
            </span>
          </button>
        </div>
        <div class="w-full h-full overflow-x-hidden">
          <BaseTable
            v-if="list"
            table-id="sales-persons-dc-modal-add-table"
            :total-records="totalRecords"
            :headers="salesPersonHeaders"
            :list="list"
            :emit-selected-rows="emitSelectedRows"
            only-search
            hide-multiselect-delete
            hide-multiselect-download
            @refresh-data="onRefreshData"
            @selected-rows="onMultiselect" />
        </div>
      </div>
    </template>
    <template #saveButtonText> {{ $t('common.actions.add') }} </template>
  </BaseModalEditCreate>
  <BaseModalConfirm
    v-if="isAddFilteredModalActive"
    @cancel="isAddFilteredModalActive = false"
    @confirm="onAddAllFilteredSalesPersons">
    <template #title>{{ $t('common.actions.addAll') }}</template>
    <template #text>
      <div class="flex flex-col w-full gap-6">
        <p>{{ $t('salesperson.addAllModalText') }}</p>
        <div class="flex flex-col items-start justify-start gap-4">
          <BaseToggle
            class="text-sm"
            :label="$t('baseObject.isActive')"
            name="is-active"
            light
            v-model="addAllIsActive" />
          <p>{{ $t('salesperson.addAllModalSubText') }}</p>
        </div>
      </div>
    </template>
    <template #buttonText>{{ $t('common.actions.create') }}</template>
  </BaseModalConfirm>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import BaseModalEditCreate from '../common/BaseModalEditCreate.vue';
import BaseTable from '../common/BaseTable.vue';
import type { SalesPerson } from '@/types/SalesPersons';
import type { TableFilterData } from '@/types/Base';
import { useI18n } from 'vue-i18n';
import IconAddListOutline from '@/assets/icons/IconAddListOutline.vue';
import BaseModalConfirm from '../common/BaseModalConfirm.vue';
import BaseToggle from '../common/BaseToggle.vue';

defineProps<{
  list: SalesPerson[];
  totalRecords?: number;
}>();

const emit = defineEmits(['close', 'refreshData', 'multiSelect', 'addAllFiltered']);

const { t: $t } = useI18n({ useScope: 'global' });
const salesPersonHeaders = [
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'sequence', text: $t('salesperson.sequence'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
];

const isAddFilteredModalActive = ref(false);
const addAllIsActive = ref(true);
const tableQuery = ref<string>();

function onRefreshData(data: TableFilterData) {
  data.sortColumn = data.column ? salesPersonHeaders[data.column].value : '';
  tableQuery.value = data.query;
  emit('refreshData', data);
}

const emitSelectedRows = ref(false);
function onMultiselect(items: SalesPerson[]) {
  emit('multiSelect', items);
}

function onAddAllFilteredSalesPersons() {
  isAddFilteredModalActive.value = false;
  emit('addAllFiltered', addAllIsActive.value, tableQuery.value);
}
</script>
